import colorjs from 'color';
import { name } from 'ntcjs';

export type CColorPickerValue = FullColorObject | FullGradientObject | FullPaletteObject;

export interface FullColorObject {
    name: string;
    hex: string;
    hsl: HslColor;
    hsv: HsvColor;
    cmyk: CmykColor;
    rgb: RgbColor;
    rounded: {
        cmyk: CmykColor;
        rgb: RgbColor;
    };
    a: number;
    oldHue: number;
    typeColor?: string;
}

export const enum GradientType {
    LINEAR = 'linear',
    RADIAL = 'radial'
}

export const enum PaletteType {
    LETTER = 'letter'
}

export interface GradientStopObject {
    offset: number;
    hex: string;
    cmyk: CmykColor;
    alpha: number;
}

export interface FullGradientObject {
    colorStops: GradientStopObject[];
    type: 'linear' | 'radial';
    angle: number;
    typeColor?: string;
}

export interface PaletteColorObject {
    hex: string;
    cmyk: CmykColor;
    alpha: number;
}

export interface FullPaletteObject {
    colors: PaletteColorObject[];
    type: 'letter';
    typeColor?: string;
}

export type HslColor = Record<'h' | 's' | 'l', number>;
export type HsvColor = Record<'h' | 's' | 'v', number>;
export type RgbColor = Record<'r' | 'g' | 'b', number>;
export type CmykColor = Record<'c' | 'm' | 'y' | 'k', number>;

export const ColorPickerType = Object.freeze({
    SOLID: 'solid',
    GRADIENT: 'gradient',
    PALETTE: 'palette'
});

export type ColorPickerTypeValue = typeof ColorPickerType[keyof typeof ColorPickerType];

export const useColor = () => {
    const generateColorObj = (color: colorjs) => {
        const hsl = color.hsl().object() as HslColor;
        const hsv = color.hsv().object() as HsvColor;
        const rgb = color.rgb();
        const cmyk = color.cmyk();
        const hex = color.hex();

        const roundedRgb = rgb.round().object();
        Reflect.deleteProperty(roundedRgb, 'alpha');

        const fullColor = {
            hsl,
            hsv,
            hex: hex.toUpperCase(),
            rgb: rgb.object() as RgbColor,
            cmyk: cmyk.object() as CmykColor,
            rounded: {
                rgb: roundedRgb as RgbColor,
                cmyk: cmyk.round().object() as CmykColor
            },
            oldHue: hsl.h,
            name: hexToName(hex),
            a: color.alpha()
        };

        return fullColor;
    };

    const getColorObj = (data: Partial<FullColorObject>, oldHue?: number): FullColorObject | undefined => {
        const alpha = data?.a ?? 1;
        const initial = data.hsl || data.hex || data.hsv || data.rgb || data.cmyk || data;

        try {
            const color = colorjs(initial);
            color.alpha(alpha);

            const fullColor = generateColorObj(color);
            fullColor.a = alpha;

            if (oldHue !== undefined) {
                fullColor.oldHue = oldHue;
            }

            if (fullColor.hsl.s === 0) {
                fullColor.hsv.h = fullColor.hsl.h = data.hsl?.h || oldHue || 0;
            }

            return fullColor;
        } catch (error) {
            console.warn('Error creating color object:', error);
            return;
        }
    };

    const getColorFromString = (colorStr: string) => {
        const color = colorjs(colorStr);
        return generateColorObj(color);
    };

    const cmykToDecimalString = (cmyk: CmykColor): string => {
        return `${cmyk.c / 100},${cmyk.m / 100},${cmyk.y / 100},${cmyk.k / 100}`;
    };

    const cmykStringToObject = (cmykString: string): CmykColor => {
        const [c, m, y, k] = cmykString.split(',');
        return {
            c: parseFloat(c) * 100,
            m: parseFloat(m) * 100,
            y: parseFloat(y) * 100,
            k: parseFloat(k) * 100
        };
    };

    const isValidColor = (hex: string): boolean => {
        try {
            const color = colorjs(hex);
            return !!color;
        } catch {
            return false;
        }
    };

    const hexToHexa = (hex: string, alpha: number) => {
        const color = colorjs(hex);
        return color.alpha(alpha).hexa();
    };

    const hexToRgba = (hex: string, alpha: number) => {
        const color = colorjs(hex);
        return color.alpha(alpha).rgb().string();
    };

    const hexToName = (hex: string) => {
        const match = name(hex);
        return match[1];
    };

    const getGradientCSS = (gradientColorList: Partial<GradientStopObject>[], gradientAngle = 90) => {
        const angleCss = gradientAngle + 90; // Adjust angle to match CSS linear-gradient expectations
        const stops = gradientColorList
            .map((stop, index) => {
                const isLast = index === gradientColorList.length - 1;
                return `${stop.hex} ${stop.offset}%${isLast ? '' : ', '}`;
            })
            .join('');

        return {
            backgroundImage: `linear-gradient(${angleCss}deg, ${stops})`
        };
    };

    return {
        getColorObj,
        isValidColor,
        hexToHexa,
        hexToName,
        getColorFromString,
        cmykToDecimalString,
        cmykStringToObject,
        hexToRgba,
        getGradientCSS
    };
};
